import React from 'react';
import { saveForUserContextEncrypted, getForUserContextEncrypted, saveForUserContextUnencrypted, getForUserContextUnencrypted, } from '@famly/stat_ts-utils_local-storage';
/**
 * A hook that returns the current query params with keys matching the passed array.
 *
 * It will store values in the user context only.
 *
 * @param {Array.<string>} statLocalStorageKey - List of localStorage names who's values to get.
 * @param {UseStatLocalStorageOptions} options - Storage options. Set `encrypted: false` to store values as plain text.
 * @returns {\{statLocalStorageKey: string | null\}} - Map of the provided keys with their
 * corresponding values. Null if empty.
 *
 * Usage:
 * ```js
 * const queryVariables = useStatLocalStorage(['foo'])
 * console.log(queryVariables.foo) // Outputs "bar"
 * ```
 *
 * You can also assign new values and it will be stored in the search params:
 *
 * Usage:
 * ```js
 * const queryVariables = useStatLocalStorage(['hello'])
 * queryVariables.hello = 'world'
 * console.log(new URLSearchParams(window.location.search).hello) // Outputs "world"
 * ```
 *
 */
export function useStatLocalStorage(statLocalStorageKey, options) {
    var _a;
    const encrypted = (_a = options === null || options === void 0 ? void 0 : options.encrypted) !== null && _a !== void 0 ? _a : true;
    const computedLocalStorage = useComputeLocalStorage(statLocalStorageKey, encrypted);
    return React.useMemo(() => new Proxy(computedLocalStorage, {
        /**
         * This proxy handler overwrites the default setting of a value in the returned value here.
         *
         * Instead of setting the value in the localStorage, leading to no change, we are taking the
         * assigned value and instead save it in the search params. This in turn will lead to a
         * rerun this hook, which in turns rerenders the components using this hook.
         *
         * If you want to delete an existing query param, just pass 'undefined'
         *
         * @param _ current query parameters object. unused.
         * @param prop the property the user assigned a new value to
         * @param newValue the new value
         * @returns true, which means the assignment was successful
         */
        set(_, prop, newValue) {
            if (encrypted) {
                saveForUserContextEncrypted(prop, newValue);
            }
            else {
                saveForUserContextUnencrypted(prop, newValue);
            }
            return true;
        },
    }), [computedLocalStorage, encrypted]);
}
const useComputeLocalStorage = (statLocalStorageKey, encrypted) => {
    return React.useMemo(() => {
        return statLocalStorageKey.reduce((reduction, localStorageKey) => {
            reduction[localStorageKey] = encrypted
                ? getForUserContextEncrypted(localStorageKey)
                : getForUserContextUnencrypted(localStorageKey);
            return reduction;
        }, {});
    }, [statLocalStorageKey, encrypted]);
};
